import {
  QDropdownWrapper,
  QDataTable,
  QModal,
  QModalContent,
  QModalTitle,
  QTypography,
  QButton,
  QTextField,
  QModalActions,
} from '@kw/quokka-ui';
import { Avatar, Box } from '@mui/material';
import { Close } from '@mui/icons-material';
import _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';

import { useLocalStorage } from '../../utils/useLocalStorage';
import { useFetchService, fetchService } from '../../utils/fetchService';
import { OrganizationDropdown } from './OrganizationDropdown';
import { sendHttpRequest } from '../../utils/network.service';
import { generatePassword } from './utils';

const userTypeOptions = [
  { label: 'PDM', value: ['Applications', 'Basic', 'Devices'] as string[] },
  { label: 'MDM', value: ['Applications', 'Basic', 'MDM'] as string[] },
];

export const Users = () => {
  const region = process.env.AUTH0_AUDIENCE_URL === 'https://ch.api.krwr.net' ? 'eu' : 'us';

  const { enqueueSnackbar } = useSnackbar();
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const usersPath = `/organizations/global/users/list?orgId=${selectedOrganization === 'all' ? '' : selectedOrganization}`;
  const { data: usersResp, mutate } = useFetchService(usersPath, accessToken);

  const [isUserDetailsModalOpen, setIsUserDetailsModalOpen] = useState(false);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [userDetails, setUserDetails] = useState<any>(null);

  const [newUserEmail, setNewUserEmail] = useState('');
  const [selectedUserType, setSelectedUserType] = useState<string[]>([]);
  const [generatedPassword, setGeneratedPassword] = useState<string>('');

  const [isAssignNewOrgModalOpen, setIsAssignNewOrgModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedNewOrg, setSelectedNewOrg] = useState<string>('');

  const users = (usersResp as any)?.users ?? [];

  const emailValid = /\S+@\S+\.\S+/.test(newUserEmail.trim());
  const orgSelected = Boolean(selectedOrganization && selectedOrganization !== 'all');
  const canOpenCreate = orgSelected;
  const canCreateUser = emailValid && selectedUserType.length > 0;

  const closeUserDetails = useCallback(() => setIsUserDetailsModalOpen(false), []);
  const openUserDetails = useCallback(() => setIsUserDetailsModalOpen(true), []);
  const toggleCreateUserModal = useCallback(() => setIsCreateUserModalOpen(v => !v), []);
  const toggleAssignNewOrgModal = useCallback(() => setIsAssignNewOrgModalOpen(v => !v), []);

  const fetchUserDetails = useCallback(
    async (userEmail: string) => {
      try {
        return await fetchService(`/organizations/global/users?email=${encodeURIComponent(userEmail)}`, accessToken);
      } catch (e) {
        enqueueSnackbar('Failed to fetch user details.', { variant: 'error' });
        return null;
      }
    },
    [accessToken, enqueueSnackbar],
  );

  const fetchUserRoles = useCallback(
    async (userId: string) => {
      try {
        return await fetchService(`/organizations/global/users/${userId}/roles`, accessToken);
      } catch {
        enqueueSnackbar('Failed to fetch user roles.', { variant: 'error' });
        return null;
      }
    },
    [accessToken, enqueueSnackbar],
  );

  const getUserDetails = useCallback(
    async user => {
      const [details, roles] = await Promise.all([fetchUserDetails(user.email), fetchUserRoles(user.user_id)]);
      setUserDetails({ details, roles });
      openUserDetails();
    },
    [fetchUserDetails, fetchUserRoles, openUserDetails],
  );

  const createUser = useCallback(async () => {
    if (!canCreateUser || !orgSelected) return;

    const userBody = {
      email: newUserEmail.trim(),
      roles: selectedUserType,
      emailVerified: true,
      organizationId: selectedOrganization,
      connection: region === 'eu' ? 'Username-Password-Authentication' : 'email',
      password: region === 'eu' ? generatedPassword : undefined,
    };

    await sendHttpRequest({
      path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/global/users`,
      method: 'POST',
      body: userBody,
      successMessage: 'User created successfully',
      errorMessage: 'Error creating user',
      enqueueSnackbar,
    });

    setNewUserEmail('');
    setSelectedUserType([]);
    setGeneratedPassword('');
    toggleCreateUserModal();
    mutate();
  }, [
    canCreateUser,
    orgSelected,
    newUserEmail,
    selectedUserType,
    selectedOrganization,
    region,
    generatedPassword,
    enqueueSnackbar,
    mutate,
    toggleCreateUserModal,
  ]);

  const handleAssignOrganization = useCallback(
    async user => {
      if (!selectedNewOrg || selectedNewOrg === 'all') {
        enqueueSnackbar('Select a valid organization.', { variant: 'warning' });
        return;
      }

      await sendHttpRequest({
        path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/global/users/${user.user_id}/assign-org`,
        method: 'PATCH',
        body: { orgId: selectedNewOrg },
        successMessage: 'User assigned to organization successfully',
        errorMessage: 'Error assigning user to organization',
        enqueueSnackbar,
      });

      await sendHttpRequest({
        path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/global/users/${user.user_id}/add-roles`,
        method: 'POST',
        body: { roles: [] },
        successMessage: 'User roles updated successfully',
        errorMessage: 'Error updating user roles',
        enqueueSnackbar,
      });

      setSelectedNewOrg('');
      toggleAssignNewOrgModal();
      mutate();
    },
    [enqueueSnackbar, mutate, selectedNewOrg, toggleAssignNewOrgModal],
  );

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        renderCell: row => (
          <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src={row.picture} alt={row.name || row.email} />
            <span onClick={() => getUserDetails(row)}>{row.name || '(no name)'}</span>
          </Box>
        ),
      },
      { field: 'user_id', headerName: 'User ID' },
      { field: 'email', headerName: 'Email' },
      {
        field: 'app_metadata.organizationId',
        headerName: 'Organization ID',
        renderCell: row =>
          row.app_metadata?.organizationId ? (
            row.app_metadata.organizationId
          ) : (
            <QButton
              variant="outlined"
              onClick={() => {
                setSelectedUser(row);
                toggleAssignNewOrgModal();
              }}
            >
              Assign Organization
            </QButton>
          ),
      },
      { field: 'last_login', headerName: 'Last Login' },
      { field: 'logins_count', headerName: 'Login Count' },
      { field: 'created_at', headerName: 'Created At' },
      { field: 'updated_at', headerName: 'Updated At' },
    ],
    [getUserDetails, toggleAssignNewOrgModal],
  );

  const createUserButtonClick = useCallback(() => {
    const pw = region === 'eu' ? generatePassword() : '';
    setGeneratedPassword(pw);
    toggleCreateUserModal();
  }, [region, toggleCreateUserModal]);

  return (
    <>
      {/* User details modal */}
      <QModal
        maxWidth="xl"
        open={isUserDetailsModalOpen}
        title={
          <QModalTitle onClose={closeUserDetails} closeIcon={<Close />}>
            User Details
          </QModalTitle>
        }
        content={
          <QModalContent>
            {userDetails ? (
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(userDetails, null, 2)}</pre>
            ) : (
              <QTypography>No user details found</QTypography>
            )}
          </QModalContent>
        }
      />

      {/* Create user modal */}
      <QModal
        open={isCreateUserModalOpen}
        title={
          <QModalTitle onClose={toggleCreateUserModal} closeIcon={<Close />}>
            Create User
          </QModalTitle>
        }
        content={
          <QModalContent>
            <QTextField label="User Email" value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)} fullWidth />

            {region === 'eu' && generatedPassword && (
              <Box mt={2} mb={1}>
                <QTextField label="Generated Password" value={generatedPassword} disabled fullWidth />
              </Box>
            )}

            <QDropdownWrapper
              closedLabel={userTypeOptions.find(o => _.isEqual(o.value, selectedUserType))?.label ?? 'Select User Type'}
              selectedOption={selectedUserType}
              options={userTypeOptions}
              formControlProps={{ sx: { width: '100%' } }}
              onChange={e => setSelectedUserType(e.target.value as string[])}
            />
          </QModalContent>
        }
        actions={
          <QModalActions>
            <QButton variant="outlined" onClick={toggleCreateUserModal}>
              Close
            </QButton>
            <QButton variant="filled" onClick={createUser} disabled={!canCreateUser}>
              Create User
            </QButton>
          </QModalActions>
        }
      />

      {/* Assign org modal */}
      <QModal
        maxWidth="xl"
        open={isAssignNewOrgModalOpen}
        title={
          <QModalTitle onClose={toggleAssignNewOrgModal} closeIcon={<Close />}>
            Assign Organization
          </QModalTitle>
        }
        content={
          <QModalContent>
            <QTypography>User: {selectedUser?.email ?? ''}</QTypography>
            <OrganizationDropdown setSelectedOrganization={setSelectedNewOrg} />
          </QModalContent>
        }
        actions={
          <QModalActions>
            <QButton variant="outlined" onClick={toggleAssignNewOrgModal}>
              Close
            </QButton>
            <QButton
              variant="filled"
              disabled={!selectedNewOrg || selectedNewOrg === 'all' || !selectedUser}
              onClick={() => selectedUser && handleAssignOrganization(selectedUser)}
            >
              Assign Organization
            </QButton>
          </QModalActions>
        }
      />

      {/* Filters / actions */}
      <OrganizationDropdown setSelectedOrganization={setSelectedOrganization} hasAllOption />

      <Box p={2}>
        <QButton variant="filled" onClick={createUserButtonClick} disabled={!canOpenCreate}>
          Create User
        </QButton>
      </Box>

      {/* Users table */}
      <QDataTable emptyTableText="No Users Found" columns={columns} data={users} searchConfig={['user_id', 'name', 'email']} />
    </>
  );
};
